// frontend/src/components/analytics/ReportsPage.js
// Admin report generator — period summary with previous-period comparison,
// Excel export (multi-sheet) and print/PDF via window.print().
import React, { useState } from 'react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Button,
  ButtonGroup,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Stack
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import GetAppIcon from '@mui/icons-material/GetApp';
import PrintIcon from '@mui/icons-material/Print';
import api from '../../services/api';

const printStyles = `
@media print {
  body * {
    visibility: hidden;
  }
  #report-content, #report-content * {
    visibility: visible;
  }
  #report-content {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    margin: 0;
    padding: 16px;
    box-shadow: none !important;
  }
}
`;

const fmtDate = (d) => (d ? dayjs(d).format('DD MMM YYYY') : 'N/A');
const num = (v) => (v === null || v === undefined ? 0 : Number(v));
const fixed1 = (v) => (v === null || v === undefined ? '–' : Number(v).toFixed(1));

// Delta arrow with % change vs previous period.
const Delta = ({ current, previous, invert = false }) => {
  const cur = num(current);
  const prev = num(previous);
  if (prev === 0 && cur === 0) return <Typography variant="caption" color="text.secondary">–</Typography>;
  const diff = cur - prev;
  const pct = prev === 0 ? 100 : Math.abs((diff / prev) * 100);
  if (diff === 0) {
    return <Typography variant="caption" color="text.secondary">no change</Typography>;
  }
  const up = diff > 0;
  // For "bad" metrics (e.g. pending) an increase is negative — invert the color.
  const good = invert ? !up : up;
  return (
    <Typography variant="caption" sx={{ color: good ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
      {up ? '▲' : '▼'} {Math.abs(diff)} ({pct.toFixed(0)}%)
    </Typography>
  );
};

const KpiBox = ({ title, current, previous, invert }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%', textAlign: 'center' }}>
    <Typography variant="body2" color="text.secondary">
      {title}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>
      {num(current)}
    </Typography>
    <Box>
      <Delta current={current} previous={previous} invert={invert} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        prev: {num(previous)}
      </Typography>
    </Box>
  </Paper>
);

const SectionTable = ({ title, headers, rows, renderRow }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map((h, i) => (
              <TableCell key={h} sx={{ fontWeight: 600 }} align={i === 0 ? 'left' : 'right'}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length > 0 ? (
            rows.map(renderRow)
          ) : (
            <TableRow>
              <TableCell colSpan={headers.length} align="center">
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const ReportsPage = () => {
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const applyPreset = (preset) => {
    const today = dayjs();
    if (preset === 'thisMonth') {
      setStartDate(today.startOf('month'));
      setEndDate(today);
    } else if (preset === 'lastMonth') {
      const lastMonth = today.subtract(1, 'month');
      setStartDate(lastMonth.startOf('month'));
      setEndDate(lastMonth.endOf('month'));
    } else if (preset === 'thisQuarter') {
      const quarterStartMonth = Math.floor(today.month() / 3) * 3;
      setStartDate(today.month(quarterStartMonth).startOf('month'));
      setEndDate(today);
    }
    // 'custom' — leave the pickers as they are for manual selection.
  };

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/complaints/reports/summary', {
        params: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD')
        }
      });
      setReport(res.data);
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const current = report?.current || {};
  const previous = report?.previous || {};
  const categoryStats = Array.isArray(current.categoryStats) ? current.categoryStats : [];
  const assemblyStats = (Array.isArray(current.assemblyStats) ? current.assemblyStats : [])
    .slice()
    .sort((a, b) => (b?.count || 0) - (a?.count || 0))
    .slice(0, 10);
  const staffWorkload = Array.isArray(current.staffWorkloadAssigned) ? current.staffWorkloadAssigned : [];
  const categoryPerformance = Array.isArray(current.categoryPerformance) ? current.categoryPerformance : [];

  const handleDownloadExcel = () => {
    if (!report) return;
    const wb = XLSX.utils.book_new();

    const summaryRows = [
      { Metric: 'Period Start', Value: fmtDate(report.period?.start) },
      { Metric: 'Period End', Value: fmtDate(report.period?.end) },
      { Metric: 'Total Complaints', Value: num(current.totalComplaints), Previous: num(previous.totalComplaints) },
      { Metric: 'New Complaints', Value: num(current.newComplaints), Previous: num(previous.newComplaints) },
      { Metric: 'Open Complaints', Value: num(current.openComplaints), Previous: num(previous.openComplaints) },
      { Metric: 'Pending Complaints', Value: num(current.pendingComplaints), Previous: num(previous.pendingComplaints) },
      { Metric: 'Resolved Complaints', Value: num(current.resolvedComplaints), Previous: num(previous.resolvedComplaints) },
      {
        Metric: 'Avg Resolution Days',
        Value: fixed1(current.resolutionStats?.avgDays),
        Previous: fixed1(previous.resolutionStats?.avgDays)
      },
      {
        Metric: 'Reopen Rate (%)',
        Value: fixed1(current.reopenStats?.reopenRate),
        Previous: fixed1(previous.reopenStats?.reopenRate)
      },
      { Metric: 'Total Reopens', Value: num(current.reopenStats?.totalReopens), Previous: num(previous.reopenStats?.totalReopens) }
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'Summary');

    const categoryRows = categoryStats.map((c) => ({
      Category: c?._id || 'Unknown',
      Count: num(c?.count)
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(categoryRows), 'By Category');

    const assemblyRows = (Array.isArray(current.assemblyStats) ? current.assemblyStats : []).map((a) => ({
      Assembly: a?._id || 'Unknown',
      Count: num(a?.count)
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(assemblyRows), 'By Assembly');

    const staffRows = staffWorkload.map((s) => ({
      Name: s?.name || 'Unknown',
      Total: num(s?.total),
      Open: num(s?.open),
      Closed: num(s?.closed)
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(staffRows), 'By Staff');

    const perfRows = categoryPerformance.map((c) => ({
      Category: c?.category || 'Unknown',
      Total: num(c?.total),
      Open: num(c?.open),
      Closed: num(c?.closed),
      'Avg Resolution Days': fixed1(c?.avgResolutionDays),
      'Avg Open Age Days': fixed1(c?.avgOpenAgeDays),
      Reopens: num(c?.reopens)
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(perfRows), 'Category Performance');

    const fileName = `complaint_report_${startDate.format('YYYYMMDD')}_${endDate.format('YYYYMMDD')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <style>{printStyles}</style>
      <Container maxWidth={false} sx={{ width: '100%', mt: 7, p: 2, bgcolor: 'var(--bg-color)' }}>
        <Typography variant="h5" sx={{ color: 'var(--text-color)', fontWeight: 600, mb: 2 }}>
          Reports
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* Controls */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
          <ButtonGroup variant="outlined" size="small">
            <Button onClick={() => applyPreset('thisMonth')}>This Month</Button>
            <Button onClick={() => applyPreset('lastMonth')}>Last Month</Button>
            <Button onClick={() => applyPreset('thisQuarter')}>This Quarter</Button>
            <Button onClick={() => applyPreset('custom')}>Custom</Button>
          </ButtonGroup>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
          />
          <Button variant="contained" onClick={handleGenerate} disabled={loading}>
            Generate
          </Button>
          {report && (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<GetAppIcon />} onClick={handleDownloadExcel}>
                Download Excel
              </Button>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
                Print / Save as PDF
              </Button>
            </Stack>
          )}
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress aria-label="Generating report" />
          </Box>
        )}

        {!loading && !report && !error && (
          <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Select a period and click Generate to build the report.
          </Typography>
        )}

        {!loading && report && (
          <Paper id="report-content" sx={{ p: 4, boxShadow: 3, borderRadius: 1 }}>
            {/* Report header */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Complaint Management Report — Office of KC Venugopal MP
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
                Period: {fmtDate(report.period?.start)} to {fmtDate(report.period?.end)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Compared with previous period: {fmtDate(report.previousPeriod?.start)} to{' '}
                {fmtDate(report.previousPeriod?.end)} · Generated on {dayjs().format('DD MMM YYYY, HH:mm')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* KPI grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiBox title="Total Complaints" current={current.totalComplaints} previous={previous.totalComplaints} />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiBox title="New" current={current.newComplaints} previous={previous.newComplaints} />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiBox title="Open" current={current.openComplaints} previous={previous.openComplaints} invert />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiBox title="Pending" current={current.pendingComplaints} previous={previous.pendingComplaints} invert />
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <KpiBox title="Resolved" current={current.resolvedComplaints} previous={previous.resolvedComplaints} />
              </Grid>
            </Grid>

            {/* Resolution + reopen stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Resolution Statistics
                  </Typography>
                  <Typography variant="body2">
                    Average resolution time: <strong>{fixed1(current.resolutionStats?.avgDays)} days</strong>{' '}
                    (previous: {fixed1(previous.resolutionStats?.avgDays)} days)
                  </Typography>
                  <Typography variant="body2">
                    Fastest: {fixed1(current.resolutionStats?.minDays)} days · Slowest:{' '}
                    {fixed1(current.resolutionStats?.maxDays)} days
                  </Typography>
                  <Typography variant="body2">
                    Complaints resolved in period: {num(current.resolutionStats?.count)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Reopen Statistics
                  </Typography>
                  <Typography variant="body2">
                    Reopen rate: <strong>{fixed1(current.reopenStats?.reopenRate)}%</strong> (previous:{' '}
                    {fixed1(previous.reopenStats?.reopenRate)}%)
                  </Typography>
                  <Typography variant="body2">
                    Total reopens: {num(current.reopenStats?.totalReopens)} · Complaints reopened:{' '}
                    {num(current.reopenStats?.reopenedComplaints)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Tables */}
            <SectionTable
              title="Complaints by Category"
              headers={['Category', 'Count']}
              rows={categoryStats}
              renderRow={(c, idx) => (
                <TableRow key={c?._id || idx}>
                  <TableCell>{c?._id || 'Unknown'}</TableCell>
                  <TableCell align="right">{num(c?.count)}</TableCell>
                </TableRow>
              )}
            />

            <SectionTable
              title="Top 10 Assemblies"
              headers={['Assembly', 'Count']}
              rows={assemblyStats}
              renderRow={(a, idx) => (
                <TableRow key={a?._id || idx}>
                  <TableCell>{a?._id || 'Unknown'}</TableCell>
                  <TableCell align="right">{num(a?.count)}</TableCell>
                </TableRow>
              )}
            />

            <SectionTable
              title="Staff Workload (Assigned)"
              headers={['Name', 'Total', 'Open', 'Closed']}
              rows={staffWorkload}
              renderRow={(s, idx) => (
                <TableRow key={s?._id || s?.userId || idx}>
                  <TableCell>{s?.name || 'Unknown'}</TableCell>
                  <TableCell align="right">{num(s?.total)}</TableCell>
                  <TableCell align="right">{num(s?.open)}</TableCell>
                  <TableCell align="right">{num(s?.closed)}</TableCell>
                </TableRow>
              )}
            />

            <SectionTable
              title="Category Performance"
              headers={['Category', 'Total', 'Open', 'Closed', 'Avg Resolution Days', 'Avg Open Age Days', 'Reopens']}
              rows={categoryPerformance}
              renderRow={(c, idx) => (
                <TableRow key={c?.category || idx}>
                  <TableCell>{c?.category || 'Unknown'}</TableCell>
                  <TableCell align="right">{num(c?.total)}</TableCell>
                  <TableCell align="right">{num(c?.open)}</TableCell>
                  <TableCell align="right">{num(c?.closed)}</TableCell>
                  <TableCell align="right">{fixed1(c?.avgResolutionDays)}</TableCell>
                  <TableCell align="right">{fixed1(c?.avgOpenAgeDays)}</TableCell>
                  <TableCell align="right">{num(c?.reopens)}</TableCell>
                </TableRow>
              )}
            />
          </Paper>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default ReportsPage;
